/**
 * Mock bookings — one per accepted quote plus a handful of dev-provider
 * bookings spanning past, current and upcoming so the calendar/earnings
 * pages look like a real business.
 */

import type { Tables } from '@/lib/db.types'
import {
  bookingEntityId, quoteEntityId, requestEntityId, providerId, customerUserId,
  DEV_USER_IDS,
} from './ids'
import { DEV_PROVIDER } from './providers'

type Booking = Tables<'bookings'>

const NOW = Date.parse('2026-05-01T08:00:00.000Z')
const T = (days: number) => new Date(NOW + days * 86400_000).toISOString()

interface BookingSeed {
  customerId: string
  providerEntityId: string
  quoteN: number
  requestN: number
  status: Booking['status']
  scheduledIn: number // days from NOW (negative = past)
  total: number
  payment_status: Booking['payment_status']
  customer_notes?: string | null
  provider_notes?: string | null
}

const SEEDS: BookingSeed[] = [
  // Dev customer's elektriker — upcoming, paid in escrow
  { customerId: DEV_USER_IDS.customer, providerEntityId: providerId(3),
    quoteN: 4, requestN: 3,
    status: 'confirmed', scheduledIn: 4, total: 2400, payment_status: 'paid',
    customer_notes: 'Portkod 1234. Lämna meddelande om du är försenad.',
    provider_notes: null },

  // Dev customer's IKEA-PAX montering — completed
  { customerId: DEV_USER_IDS.customer, providerEntityId: providerId(15),
    quoteN: 6, requestN: 4,
    status: 'completed', scheduledIn: -14, total: 1900, payment_status: 'paid',
    customer_notes: null,
    provider_notes: 'Klart inom 2.5h. Kund mycket nöjd.' },

  // Dev customer's målning — completed
  { customerId: DEV_USER_IDS.customer, providerEntityId: providerId(6),
    quoteN: 8, requestN: 5,
    status: 'completed', scheduledIn: -50, total: 8500, payment_status: 'paid',
    customer_notes: null,
    provider_notes: 'Två lager appliceras. Kund nöjd, lät oss måla även hallen extra.' },

  // Other customer's kakling — in-progress
  { customerId: customerUserId(5), providerEntityId: providerId(17),
    quoteN: 11, requestN: 10,
    status: 'in_progress', scheduledIn: -1, total: 24500, payment_status: 'paid',
    customer_notes: 'Stora plattor till golvet (60x60). Liten plattor till väggen.',
    provider_notes: 'Dag 1 av 5 — väggar klart, golv resterande.' },

  // Other customer's datorhjälp — completed
  { customerId: customerUserId(6), providerEntityId: providerId(9),
    quoteN: 13, requestN: 11,
    status: 'completed', scheduledIn: -12, total: 890, payment_status: 'paid',
    customer_notes: null, provider_notes: 'Fjärrsupport, ca 45 min.' },

  // Other customer's matteundervisning — completed
  { customerId: customerUserId(1), providerEntityId: providerId(10),
    quoteN: 16, requestN: 14,
    status: 'completed', scheduledIn: -35, total: 3200, payment_status: 'paid',
    customer_notes: null, provider_notes: '8 lektioner avslutade. Mycket bra resultat på provet.' },

  // ── Dev provider bookings (so provider dashboard is alive) ─────────
  // Past — completed, paid out
  { customerId: customerUserId(2), providerEntityId: DEV_PROVIDER.id,
    quoteN: 7, requestN: 1,
    status: 'completed', scheduledIn: -38, total: 4200, payment_status: 'paid',
    customer_notes: null, provider_notes: 'Snabbt jobb, kund gav 5/5.' },
  { customerId: customerUserId(3), providerEntityId: DEV_PROVIDER.id,
    quoteN: 7, requestN: 6,
    status: 'completed', scheduledIn: -22, total: 6800, payment_status: 'paid',
    customer_notes: null, provider_notes: 'Större hantverksjobb. Klart på utsatt tid.' },
  { customerId: customerUserId(4), providerEntityId: DEV_PROVIDER.id,
    quoteN: 7, requestN: 9,
    status: 'completed', scheduledIn: -11, total: 9200, payment_status: 'paid',
    customer_notes: null, provider_notes: 'TV-bänk monterad enligt skiss.' },

  // Confirmed — upcoming jobs (calendar lights up)
  { customerId: customerUserId(7), providerEntityId: DEV_PROVIDER.id,
    quoteN: 16, requestN: 12,
    status: 'confirmed', scheduledIn: 2, total: 3200, payment_status: 'paid',
    customer_notes: 'Är hemma efter 16:00.', provider_notes: null },
  { customerId: customerUserId(1), providerEntityId: DEV_PROVIDER.id,
    quoteN: 16, requestN: 14,
    status: 'confirmed', scheduledIn: 6, total: 5400, payment_status: 'paid',
    customer_notes: 'Brick + cement på plats.', provider_notes: null },
  { customerId: customerUserId(2), providerEntityId: DEV_PROVIDER.id,
    quoteN: 16, requestN: 15,
    status: 'confirmed', scheduledIn: 12, total: 4800, payment_status: 'pending',
    customer_notes: null, provider_notes: null },

  // In progress (today)
  { customerId: customerUserId(5), providerEntityId: DEV_PROVIDER.id,
    quoteN: 16, requestN: 10,
    status: 'in_progress', scheduledIn: 0, total: 7600, payment_status: 'paid',
    customer_notes: null, provider_notes: 'Halvvägs igenom. Skickar uppdatering ikväll.' },

  // Cancelled (with reason) so cancelled tab also has data
  { customerId: customerUserId(8), providerEntityId: DEV_PROVIDER.id,
    quoteN: 16, requestN: 13,
    status: 'cancelled', scheduledIn: -3, total: 2400, payment_status: 'refunded',
    customer_notes: null, provider_notes: 'Kund avbokade dagen innan — annan tjänst valdes.' },
]

export const MOCK_BOOKINGS: Booking[] = SEEDS.map((s, i) => {
  const n = i + 1
  const PLATFORM_RATE = 0.12
  const platformFee = Math.round(s.total * PLATFORM_RATE)
  const payout = s.total - platformFee
  const scheduledAt = T(s.scheduledIn)
  const createdAt = T(Math.min(s.scheduledIn - 3, -1))
  return {
    id: bookingEntityId(n),
    quote_id: quoteEntityId(s.quoteN),
    request_id: requestEntityId(s.requestN),
    customer_id: s.customerId,
    provider_id: s.providerEntityId,
    status: s.status,
    scheduled_at: scheduledAt,
    completed_at: s.status === 'completed' ? T(s.scheduledIn + 0.2) : null,
    cancelled_at: s.status === 'cancelled' ? T(s.scheduledIn - 1) : null,
    cancel_reason: s.status === 'cancelled' ? 'Kunden valde annan utförare' : null,
    cancelled_by: s.status === 'cancelled' ? s.customerId : null,
    total_price: s.total,
    platform_fee: platformFee,
    provider_payout: payout,
    payment_intent_id: `pi_mock_${n.toString().padStart(4, '0')}`,
    payment_status: s.payment_status,
    customer_notes: s.customer_notes ?? null,
    provider_notes: s.provider_notes ?? null,
    created_at: createdAt,
    updated_at: createdAt,
  } satisfies Booking
})

export function findBookingsByCustomerId(customerId: string): Booking[] {
  return MOCK_BOOKINGS.filter((b) => b.customer_id === customerId)
}

export function findBookingsByProviderId(providerEntityId: string): Booking[] {
  return MOCK_BOOKINGS.filter((b) => b.provider_id === providerEntityId)
}

export function findBookingById(id: string): Booking | undefined {
  return MOCK_BOOKINGS.find((b) => b.id === id)
}
